/**
 * Workforce Hub W5 — read-only Access tab.
 *
 * Surfaces an employee-scoped view of the linked user record:
 *   - account status (active / inactive)
 *   - role
 *   - permissions summary
 *   - last login + last active timestamps
 *   - assigned locations
 *
 * Edit / reset / deactivate actions stay on the standalone /users page
 * by design — this tab is a lookup surface during the W5 phase, not a
 * second admin shell.
 *
 * Permissions:
 *   The tab itself is only mounted when the caller has the
 *   `manage_users` permission (see EmployeeProfilePage). The API
 *   layer re-scopes by req.user.companyId on every read, so even a
 *   gate bypass cannot return cross-tenant rows.
 *
 * Telemetry:
 *   tab.open eventSubtype='access' is emitted by the page-level
 *   handleTabChange wrapper. This component records nothing on its
 *   own — user records are PII-adjacent.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import Pill from '../../components/ui/Pill';
import { ApiError, apiRequest } from '../../services/apiClient';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusRow({ label, children, testId }) {
  return (
    <div className="employee-access__row" data-testid={testId}>
      <div className="employee-access__row-label">{label}</div>
      <div className="employee-access__row-value">{children}</div>
    </div>
  );
}

export default function EmployeeAccessTab({ userId }) {
  const [linkedUser, setLinkedUser] = useState(null);
  const [status, setStatus] = useState(userId ? 'loading' : 'no-link');
  const [error, setError] = useState('');
  const cancelRef = useRef({ cancelled: false });

  const numericUserId = Number(userId);
  const userIdValid = Number.isFinite(numericUserId) && numericUserId > 0;

  const refetch = useCallback(async () => {
    if (!userIdValid) {
      setStatus('no-link');
      setLinkedUser(null);
      return;
    }
    cancelRef.current.cancelled = true;
    const token = { cancelled: false };
    cancelRef.current = token;
    setStatus('loading');
    setError('');
    try {
      const data = await apiRequest(`/api/users/${numericUserId}`);
      if (token.cancelled) return;
      setLinkedUser(data || null);
      setStatus(data ? 'loaded' : 'not-found');
    } catch (err) {
      if (token.cancelled) return;
      if (err instanceof ApiError && err.status === 403) {
        setLinkedUser(null);
        setStatus('forbidden');
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setLinkedUser(null);
        setStatus('not-found');
        return;
      }
      setError(err?.message || 'Unable to load linked user account.');
      setStatus('error');
    }
  }, [userIdValid, numericUserId]);

  useEffect(() => {
    refetch();
    return () => {
      cancelRef.current.cancelled = true;
    };
  }, [refetch]);

  if (status === 'no-link' || status === 'not-found') {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-access-no-link">
        <EmptyState
          icon="·"
          headline="No linked user account"
          body="This employee profile is not linked to a system login. Create or attach an account from the standalone Users & Access page."
          primaryCtaLabel="Open Users & Access"
          onPrimaryCta={() => {
            if (typeof window !== 'undefined') {
              window.location.href = '/users';
            }
          }}
        />
      </Card>
    );
  }

  if (status === 'forbidden') {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-access-forbidden">
        <EmptyState
          icon="·"
          headline="Access restricted"
          body="You don't have permission to view this employee's linked account. Ask an admin if you need access."
        />
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card padding="md" shadow="sm">
        <StatusBanner kind="error" message={error} onRetry={refetch} />
      </Card>
    );
  }

  if (status === 'loading' && !linkedUser) {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-access-loading">
        <LoadingSkeleton variant="paragraph" lines={4} />
      </Card>
    );
  }

  const isActive = Boolean(linkedUser?.isActive);
  const permissions = Array.isArray(linkedUser?.permissionKeys) ? linkedUser.permissionKeys : [];
  const assignedLocations = Array.isArray(linkedUser?.assignedLocations) ? linkedUser.assignedLocations : [];

  return (
    <Card padding="md" shadow="sm" data-testid="employee-access-panel">
      <header className="employee-access__head">
        <h2 className="employee-access__title">Linked account</h2>
        <Pill
          variant={isActive ? 'success' : 'neutral'}
          data-testid="employee-access-status-pill"
        >
          {isActive ? 'Active' : 'Inactive'}
        </Pill>
        {linkedUser?.mustChangePassword ? (
          <Pill variant="warning" data-testid="employee-access-must-change-pill">
            Password reset required
          </Pill>
        ) : null}
      </header>

      <div className="employee-access__rows">
        <StatusRow label="Email" testId="employee-access-email">
          {linkedUser?.email || '—'}
        </StatusRow>
        <StatusRow label="Role" testId="employee-access-role">
          {linkedUser?.role || '—'}
        </StatusRow>
        <StatusRow label="Last login" testId="employee-access-last-login">
          {formatDateTime(linkedUser?.lastLoginAt)}
        </StatusRow>
        <StatusRow label="Last active" testId="employee-access-last-active">
          {formatDateTime(linkedUser?.lastActiveAt)}
        </StatusRow>
        <StatusRow label="Permissions" testId="employee-access-permissions">
          {permissions.length === 0 ? (
            <span className="employee-access__muted">No additional permissions</span>
          ) : (
            <ul className="employee-access__permission-list">
              {permissions.map(key => (
                <li key={key} data-testid={`employee-access-permission-${key}`}>
                  {key}
                </li>
              ))}
            </ul>
          )}
        </StatusRow>
        <StatusRow label="Locations" testId="employee-access-locations">
          {assignedLocations.length === 0 ? (
            <span className="employee-access__muted">No assigned locations</span>
          ) : (
            <ul className="employee-access__location-list">
              {assignedLocations.map((location, idx) => (
                <li key={`${location.locationId || 'na'}-${idx}`}>
                  {location.locationName || location.locationCode || `Location #${location.locationId}`}
                </li>
              ))}
            </ul>
          )}
        </StatusRow>
      </div>

      <footer className="employee-access__footer">
        <a
          className="ui-button ui-button--sm ui-button--secondary"
          href="/users"
          data-testid="employee-access-manage-link"
        >
          Manage on Users &amp; Access
        </a>
      </footer>
    </Card>
  );
}
